import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, CalendarDays, Users, Plane, Hotel, Car, Receipt, Wallet } from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import TravelersSection from "@/sections/TravelersSection";
import FlightsSection from "@/sections/FlightsSection";
import HotelsSection from "@/sections/HotelsSection";
import CarsSection from "@/sections/CarsSection";
import ReceiptsSection from "@/sections/ReceiptsSection";

export default function TripDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [trip, setTrip] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("travelers");

  const loadTrip = useCallback(async () => {
    try {
      const { data } = await api.get(`/trips/${id}`);
      setTrip(data);
    } catch (e) {
      toast.error(formatError(e));
      nav("/");
    }
  }, [id, nav]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get(`/trips/${id}/summary`);
      setSummary(data);
    } catch (e) {
      console.warn("Summary load failed:", e?.message || e);
    }
  }, [id]);

  useEffect(() => { loadTrip(); loadSummary(); }, [loadTrip, loadSummary]);

  if (!trip) return <div className="h-64 rounded-2xl bg-muted animate-pulse" />;

  const tabs = [
    { key: "travelers", label: "Travelers", icon: Users, count: summary?.counts?.travelers },
    { key: "flights", label: "Flights", icon: Plane, count: summary?.counts?.flights },
    { key: "hotels", label: "Hotels", icon: Hotel, count: summary?.counts?.hotels },
    { key: "cars", label: "Car rentals", icon: Car, count: summary?.counts?.cars },
    { key: "receipts", label: "Receipts", icon: Receipt, count: summary?.counts?.receipts },
  ];

  return (
    <div className="space-y-8" data-testid="trip-detail">
      <button onClick={() => nav("/")} className="text-sm text-stone-600 hover:text-primary flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to trips
      </button>

      {/* Hero */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 relative rounded-2xl overflow-hidden border border-border h-64 bg-muted">
          <AuthImage fileId={trip.cover_file_id} className="w-full h-full object-cover" fallbackClassName="w-full h-full" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 text-white">
            <div className="text-xs uppercase tracking-widest opacity-80">{trip.trip_type}</div>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight mt-1" data-testid="trip-title">{trip.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm opacity-90">
              <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{trip.destination}</div>
              {(trip.start_date || trip.end_date) && (
                <div className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{trip.start_date || "?"} — {trip.end_date || "?"}</div>
              )}
            </div>
          </div>
        </div>

        <Card className="p-5 rounded-2xl border-border" data-testid="expense-summary">
          <div className="flex items-center gap-2 text-stone-500 text-xs uppercase tracking-widest"><Wallet className="w-4 h-4" /> Expenses</div>
          <div className="mt-3 space-y-2">
            {summary && Object.keys(summary.by_currency || {}).length > 0 ? (
              Object.entries(summary.by_currency).map(([cur, amt]) => (
                <div key={cur} className="flex justify-between items-baseline">
                  <span className="font-display text-2xl">{amt.toLocaleString()}</span>
                  <span className="text-sm text-stone-500">{cur}</span>
                </div>
              ))
            ) : (
              <div className="text-sm text-stone-500">No receipts yet.</div>
            )}
          </div>
          {summary && Object.keys(summary.by_category || {}).length > 0 && (
            <div className="mt-4 pt-4 border-t border-border space-y-1.5">
              {Object.entries(summary.by_category).map(([cat, amt]) => (
                <div key={cat} className="flex justify-between text-sm">
                  <span className="capitalize text-stone-600">{cat}</span>
                  <span className="font-medium">{amt.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted rounded-full p-1 flex flex-wrap h-auto">
          {tabs.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-2 px-4"
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              {t.count > 0 && <span className="text-xs opacity-70">({t.count})</span>}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="travelers"><TravelersSection tripId={id} onChange={loadSummary} /></TabsContent>
        <TabsContent value="flights"><FlightsSection tripId={id} onChange={loadSummary} /></TabsContent>
        <TabsContent value="hotels"><HotelsSection tripId={id} onChange={loadSummary} /></TabsContent>
        <TabsContent value="cars"><CarsSection tripId={id} onChange={loadSummary} /></TabsContent>
        <TabsContent value="receipts"><ReceiptsSection tripId={id} onChange={loadSummary} /></TabsContent>
      </Tabs>
    </div>
  );
}
